import { appContent, recentChecks } from "../content";
import { bindJimListening, jimHeadMarkup, setJimMood, setJimVisual, startJimIdleLoop, staticJimHeadMarkup, stopJimIdleLoop } from "../jim";
import { mobileWorkspaceNav } from "../layout";
import { confidenceBand, initialSearchResultsMarkup, noSearchResultsMarkup, searchResultCardMarkup } from "../searchResults";
import type { AnswerMode, ResultFeedbackAction, ResultFeedbackVerdict, SearchResult } from "../types";
import { escapeHtml, randomBetween } from "../utils";

type SearchPayload = {
  searchEventId: string;
  searchSessionId: string;
  message?: string;
  results: SearchResult[];
};

type SearchScreenOptions = {
  prefillQuery?: string;
  answerMode: AnswerMode;
  renderWorkspace: (content: string) => void;
  bindWorkspaceNavigation: () => void;
  setAnswerMode: (mode: AnswerMode) => void;
  openSearch: (prefillQuery?: string) => void;
  saveAnswer: (question: string, summary: string, clauses: string[]) => void;
  openClause: (clauseId: string) => void;
  search: (query: string, searchSessionId?: string, refinedFromSearchEventId?: string) => Promise<SearchPayload>;
  sendResultFeedback: (searchEventId: string, clauseId: string, verdict: ResultFeedbackAction) => Promise<{ verdict: ResultFeedbackVerdict | null }>;
};

export function renderSearchScreen(options: SearchScreenOptions): void {
  options.renderWorkspace(`
    <section class="search-workspace">
      <div class="mobile-ask-intro">
        <div class="mobile-jim-frame">
          ${jimHeadMarkup("idle")}
        </div>
        <div>
          <h1>${appContent.search.mobileHeading}</h1>
          <p>${appContent.search.mobileBody}</p>
        </div>
      </div>
      <div class="desktop-main-panel">
        <div class="desktop-workspace-hero"><h1>${appContent.search.desktopHeading}</h1></div>
        <form id="searchForm" class="searchbar">
          <span class="searchbar-icon" aria-hidden="true">⌕</span>
          <input name="query" value="${escapeHtml(options.prefillQuery || "")}" placeholder="${appContent.search.placeholder}" autocomplete="off" required />
          <button type="submit">${appContent.search.submit}</button>
        </form>
        <div class="mode-toggle" aria-label="Answer mode">
          <span>${appContent.search.modeLabel}</span>
          <button class="${options.answerMode === "plain" ? "active" : ""}" type="button" data-answer-mode="plain">${appContent.search.modes.plain}</button>
          <button class="${options.answerMode === "strict" ? "active" : ""}" type="button" data-answer-mode="strict">${appContent.search.modes.strict}</button>
          <button class="${options.answerMode === "teach" ? "active" : ""}" type="button" data-answer-mode="teach">${appContent.search.modes.teach}</button>
        </div>
        <div id="results" class="results workspace-panel">${initialSearchResultsMarkup()}</div>
      </div>
      <aside class="field-tip-panel">
        <div class="field-tip-heading">
          <div>
            <span class="eyebrow">${appContent.search.fieldTipLabel}</span>
            <h2>${appContent.search.fieldTipHeading}</h2>
          </div>
        </div>
        <div class="field-tip-image">
          ${staticJimHeadMarkup("thinking", "desktop-field-tip-head")}
        </div>
        <div class="site-note-card">
          <h3>${appContent.search.siteNoteHeading}</h3>
          <p>${appContent.search.siteNoteBody}</p>
        </div>
        <div class="why-card">
          <span class="eyebrow">${appContent.search.whyLabel}</span>
          ${appContent.search.whyItems.map((item, index) => `<p><span>${index + 1}</span> ${item}</p>`).join("")}
        </div>
      </aside>
      <section class="recent-checks">
        <h2>${appContent.search.recentChecksHeading}</h2>
        <div>
          ${recentChecks.map((item, index) => `
            <button class="${index === 0 ? "active" : ""}" type="button" data-recent-query="${escapeHtml(item.query)}">
              <strong>${escapeHtml(item.clause)}</strong><span>${escapeHtml(item.label)}</span>
            </button>
          `).join("")}
        </div>
      </section>
      <p class="search-disclaimer">${appContent.search.searchDisclaimer}</p>
      ${mobileWorkspaceNav("search")}
    </section>
  `);
  options.bindWorkspaceNavigation();
  bindJimListening(document, () => "idle");
  startJimIdleLoop(randomBetween(7000, 15000));

  document.querySelectorAll<HTMLButtonElement>("[data-answer-mode]").forEach((button) => {
    button.addEventListener("click", () => {
      options.setAnswerMode((button.dataset.answerMode || "plain") as AnswerMode);
      document.querySelectorAll<HTMLButtonElement>("[data-answer-mode]").forEach((modeButton) => {
        modeButton.classList.toggle("active", modeButton === button);
      });
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-recent-query]").forEach((button) => {
    button.addEventListener("click", () => options.openSearch(button.dataset.recentQuery || ""));
  });

  let searchSessionId: string | undefined;
  let lastSearchEventId: string | undefined;
  document.querySelector<HTMLFormElement>("#searchForm")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    stopJimIdleLoop();
    setJimVisual("thinkingGif");
    const query = String(new FormData(event.currentTarget as HTMLFormElement).get("query") || "");
    const results = document.querySelector<HTMLDivElement>("#results");
    try {
      const payload = await options.search(query, searchSessionId, lastSearchEventId);
      searchSessionId = payload.searchSessionId;
      lastSearchEventId = payload.searchEventId;
      if (results) {
        results.innerHTML = payload.results.length ? payload.results.map(searchResultCardMarkup).join("") : noSearchResultsMarkup(payload.message);
        const feedbackState = new Map<string, ResultFeedbackVerdict>();
        const topResult = payload.results[0];
        if (topResult && confidenceBand(topResult.confidence) === "low") setJimMood("thinking");
        else setJimVisual("ideaGif");
        results.querySelectorAll<HTMLButtonElement>(".save-result-button").forEach((button) => {
          button.addEventListener("click", () => options.saveAnswer(query, button.dataset.resultTitle || "", [button.dataset.clauseNumber || ""]));
        });
        results.querySelectorAll<HTMLButtonElement>(".open-clause-button").forEach((button) => button.addEventListener("click", () => options.openClause(button.dataset.clauseId || "")));
        results.querySelectorAll<HTMLButtonElement>(".result-feedback-button").forEach((button) => {
          button.addEventListener("click", async () => {
            const clauseId = button.dataset.clauseId || "";
            const tapped = button.dataset.feedbackVerdict as ResultFeedbackVerdict;
            const buttons = [...results.querySelectorAll<HTMLButtonElement>(`.result-feedback-button[data-clause-id="${CSS.escape(clauseId)}"]`)];
            const previous = feedbackState.get(clauseId) || null;
            buttons.forEach((item) => { item.disabled = true; });
            try {
              const response = await options.sendResultFeedback(payload.searchEventId, clauseId, previous === tapped ? "clear" : tapped);
              if (response.verdict) feedbackState.set(clauseId, response.verdict);
              else feedbackState.delete(clauseId);
              buttons.forEach((item) => {
                const active = item.dataset.feedbackVerdict === response.verdict;
                item.classList.toggle("active", active);
                item.setAttribute("aria-pressed", String(active));
              });
            } catch {
              buttons.forEach((item) => {
                const active = item.dataset.feedbackVerdict === previous;
                item.classList.toggle("active", active);
                item.setAttribute("aria-pressed", String(active));
              });
            } finally {
              buttons.forEach((item) => { item.disabled = false; });
            }
          });
        });
      }
      startJimIdleLoop(randomBetween(7000, 15000));
    } catch (error) {
      setJimMood("warning");
      startJimIdleLoop(randomBetween(9000, 16000));
      if (results) results.textContent = error instanceof Error ? error.message : appContent.search.searchFailure;
    }
  });
}
